import { useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  BAND_GREEN,
  BAND_LABEL,
  BAND_YELLOW,
  GRADE_NAME,
  QUALITY_AUDITS,
  TIMELINESS_WEIGHTS,
  formatGrade,
  gradeBand,
  gradeOf,
  type GradeKind,
  type StoreGrade,
} from '../../audit/grades';
import type { DistrictBreakdown } from '../../audit/gradeBreakdown';
import { PARTY_LABEL } from '../../audit/requirements';
import { longDistanceWindows } from '../longDistance';
import { daysOverdue, daysUntil, WINDOW_LABELS } from '../../audit/windows';
import type { DistrictRow, StoreRow } from '../../data/dashboard';
import { DistrictLink, StoreLink, storePath } from '../entityLinks';
import { DistrictGradeButton, StoreGradeButton } from './GradeExplain';
import { formatDay } from '../dates';
import { fitRows, PagerBar, PagerTop, useFitPageSize, usePager } from '../Pager';
import { inDays, plural } from '../plural';

/**
 * Dashboard building blocks (Phase 6a): the store board, the district
 * roll-up, the grade chip and its explanation. Shared by the home page and
 * the district pages.
 */

/**
 * Which audit a board row's clock is (owner ruling 2026-10-21: "Last audit
 * Sep 14 · Due soon" didn't say whether it was the visit or the facility
 * audit). The template's name, plus whose clock when it isn't the DM's.
 */
export function clockName(
  template: { name: string } | null,
  party: StoreRow['party'],
  /**
   * The viewer is a store team (owner ruling 2026-10-22, round 2): a DM or
   * Auditor clock still counts in their grade, but says it's someone
   * else's audit — a store can't do a "DM Visit overdue 6d" itself.
   */
  forStoreTeam = false,
): string {
  if (!template) return '';
  if (forStoreTeam && party !== 'store') {
    return `${template.name} — ${party === 'auditor' ? 'the Auditors’ audit' : 'your DM’s audit'}`;
  }
  return party && party !== 'dm' ? `${template.name} (${PARTY_LABEL[party]})` : template.name;
}

/** 'loading' / 'unavailable' = an input is missing, so no number is shown yet. */
export type GradesState = 'ready' | 'loading' | 'unavailable';

/** What a grade's parts were, for the line under the chip and screen readers. */
function partsOf(grade: StoreGrade, kind: GradeKind): string {
  if (kind === 'timeliness') return `Audits ${formatGrade(grade.onTime)} · Tasks ${formatGrade(grade.tasks)}`;
  const n = grade.scores.length;
  return n === 1 ? 'Last audit' : `Avg of last ${n} audits`;
}

/**
 * Grade chip (Timeliness or Quality): number on a green / yellow / red
 * band. Its parts show as a small line under the chip on phones and are in
 * the accessible name for screen readers. Given its board row, the chip is a
 * button that opens "Why this grade?" (GradeExplain.tsx).
 */
export function GradeChip({
  grade,
  kind,
  state = 'ready',
  row,
}: {
  grade: StoreGrade;
  kind: GradeKind;
  state?: GradesState;
  row?: StoreRow | undefined;
}) {
  if (state !== 'ready') {
    return (
      <span
        className="grade-chip grade-none"
        title={state === 'loading' ? 'Loading…' : 'Grade unavailable — some data couldn’t be loaded'}
      >
        {state === 'loading' ? '…' : 'n/a'}
      </span>
    );
  }
  const g = gradeOf(grade, kind);
  if (g === null) {
    const why =
      kind === 'timeliness'
        ? 'No timeliness grade: this store hasn’t had its first audit yet'
        : 'No quality grade: no scored audit in the last 13 months';
    return (
      <span className="grade-chip grade-none" role="img" title={why} aria-label={`No ${GRADE_NAME[kind].toLowerCase()} grade yet`}>
        —
      </span>
    );
  }
  const band = gradeBand(g);
  const parts = partsOf(grade, kind);
  const label = `${GRADE_NAME[kind]} ${formatGrade(g)}, ${BAND_LABEL[kind][band].toLowerCase()} — ${parts}`;
  return (
    <>
      {row ? (
        <StoreGradeButton row={row} kind={kind} chipClass={`grade-chip grade-${band}`} label={label} />
      ) : (
        <span className={`grade-chip grade-${band}`} role="img" title={`${BAND_LABEL[kind][band]} — ${parts}`} aria-label={label}>
          {formatGrade(g)}
        </span>
      )}
      <span className="grade-parts" aria-hidden="true">
        {parts}
      </span>
    </>
  );
}

/** A district's chip for one grade (— when no store has it yet). */
function DistrictChip({
  district,
  breakdown,
  stores,
  kind,
  behind = 0,
}: {
  district: string;
  breakdown: DistrictBreakdown<StoreRow>;
  stores: number;
  kind: GradeKind;
  /** Stores with an overdue audit (shown beside Timeliness). */
  behind?: number;
}) {
  if (breakdown[kind].grade === null) {
    return (
      <span
        className="grade-chip grade-none"
        role="img"
        aria-label={`No ${GRADE_NAME[kind].toLowerCase()} grade yet`}
        title={kind === 'timeliness' ? 'No store here has been audited yet' : 'No store here has a scored audit in the last 13 months'}
      >
        —
      </span>
    );
  }
  return <DistrictGradeButton district={district} breakdown={breakdown} stores={stores} kind={kind} behind={behind} />;
}

/** A district's two grades side by side (district page header). */
export function DistrictGrades({
  district,
  breakdown,
  stores,
  behind = 0,
}: {
  district: string;
  breakdown: DistrictBreakdown<StoreRow>;
  stores: number;
  /** Stores with an overdue audit (DistrictRow.behind). */
  behind?: number;
}) {
  return (
    <>
      Timeliness <DistrictChip district={district} breakdown={breakdown} stores={stores} kind="timeliness" behind={behind} />{' '}
      Quality <DistrictChip district={district} breakdown={breakdown} stores={stores} kind="quality" />
    </>
  );
}

/** One-line explanation of the two grades, expandable. */
export function GradeHelp() {
  const bands = (kind: GradeKind) => (
    <>
      <span className="grade-chip grade-green">{BAND_GREEN}+</span> {BAND_LABEL[kind].green.toLowerCase()} ·{' '}
      <span className="grade-chip grade-yellow">
        {BAND_YELLOW}–{BAND_GREEN - 1}
      </span>{' '}
      {BAND_LABEL[kind].yellow.toLowerCase()} · <span className="grade-chip grade-red">below {BAND_YELLOW}</span>{' '}
      {BAND_LABEL[kind].red.toLowerCase()}
    </>
  );
  return (
    <details className="grade-help">
      <summary>How the grades work</summary>
      <p>
        Each store gets two 0–100 grades, so you can tell a store in good shape but late from one
        that’s on time but failing.
      </p>
      <p>
        <strong>Timeliness</strong> — {TIMELINESS_WEIGHTS.onTime}% audits on time,{' '}
        {TIMELINESS_WEIGHTS.tasks}% open tasks not overdue: {bands('timeliness')}.
      </p>
      <p>
        <strong>Quality</strong> — the average score of the store’s last {QUALITY_AUDITS} audits (from the
        last 13 months):{' '}
        {bands('quality')}.
      </p>
      <p className="muted">
        An audit that is current or due soon scores 100. Once overdue it drops to 40, then to 0 by
        day 21 — only lateness costs points. Tasks: the share of open tasks that aren’t overdue
        (reopened ones too; none open = 100). A store with no audit yet has no grades; store
        self-audits and audits with no score don’t count toward Quality. Districts average their
        stores. Click or tap any grade to see what’s behind it.
      </p>
    </details>
  );
}

export function DistrictTable({ rows }: { rows: DistrictRow[] }) {
  return (
    <>
      <div className="table-wrap board-wrap">
        <table className="board-cards" role="table">
          <thead>
            <tr role="row">
              <th role="columnheader">District</th>
              <th role="columnheader">Timeliness</th>
              <th role="columnheader">Quality</th>
              <th role="columnheader">Stores</th>
              {/* Stores with an overdue audit (round 6, exec: "Overdue audits 6" read as a count of audits). */}
              <th role="columnheader">Stores overdue</th>
              <th role="columnheader">Open tasks</th>
              <th role="columnheader" title="Overdue tasks with no comment or update since they fell overdue">Untouched overdue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr role="row" key={`d:${d.district.toLowerCase()}`}>
                <td role="cell" className="card-full card-title">{d.district ? <DistrictLink district={d.district} /> : <span className="muted">No district</span>}</td>
                <td role="cell" data-label="Timeliness">
                  <DistrictChip district={d.district} breakdown={d.breakdown} stores={d.stores} kind="timeliness" behind={d.behind} />
                </td>
                <td role="cell" data-label="Quality">
                  <DistrictChip district={d.district} breakdown={d.breakdown} stores={d.stores} kind="quality" />
                </td>
                <td role="cell" data-label="Stores">{d.stores}</td>
                <td role="cell" data-label="Stores overdue">
                  {d.behind > 0 ? <span className="win-chip win-overdue">{d.behind}</span> : <span className="muted">0</span>}
                  {d.neverAudited > 0 && <span className="muted"> (+{d.neverAudited} not yet audited)</span>}
                </td>
                <td role="cell" data-label="Open tasks">
                  {d.openTasks}
                  {d.overdueTasks > 0 && <span className="error"> ({d.overdueTasks} overdue)</span>}
                </td>
                <td role="cell" data-label="Untouched overdue">
                  {d.untouchedOverdueTasks > 0 ? (
                    <span className="error" title="Overdue tasks nobody has commented on or updated since they fell overdue">
                      {d.untouchedOverdueTasks}
                    </span>
                  ) : (
                    <span className="muted">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Column headers' title tips don't show on touch — say it in words (exec reflect 2026-10-25). */}
      <p className="muted table-note">
        <strong>Stores overdue</strong>: stores with at least one overdue audit (not yet audited ones are
        counted apart). <strong>Untouched overdue</strong>: overdue tasks nobody has commented on or
        updated since they fell overdue — is anyone on it?
      </p>
    </>
  );
}

/** The windows a Long-distance tag lengthens at this store (DM + exec reflect 2026-10-25). */
function ldText(row: StoreRow): string {
  // Every template assigned here, started or not (QA 2026-10-25).
  const text = longDistanceWindows(row.applies);
  return text ? ` — ${text}` : '';
}

/** One clock in a board row's Audits cell: status chip, which audit, its dates. */
function ClockLine({
  clock,
  now,
  forStoreTeam,
}: {
  clock: Pick<StoreRow, 'status' | 'nextDue' | 'lastCompleted' | 'party' | 'template'>;
  now: Date;
  forStoreTeam: boolean;
}) {
  const { status, nextDue, lastCompleted, template, party } = clock;
  return (
    <div className="board-clock-line">
      <span className={`win-chip win-${status}`}>
        {WINDOW_LABELS[status]}
        {status === 'overdue' && nextDue && ` ${plural(daysOverdue(nextDue, now), 'day')}`}
      </span>
      {/* Which audit (and whose clock) the status and dates are. */}
      {template && <span className="board-clock"> {clockName(template, party, forStoreTeam)}</span>}
      {(lastCompleted || nextDue) && (
        <div className="muted cell-sub">
          {lastCompleted && <>Last {formatDay(lastCompleted)}</>}
          {lastCompleted && nextDue && ' · '}
          {nextDue && (
            <>
              {status === 'overdue' ? 'Was due' : 'Due'} {formatDay(nextDue)}
              {status !== 'overdue' && ` (${inDays(daysUntil(nextDue, now))})`}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function BoardTable({
  rows,
  now,
  gradesState = 'ready',
  forStoreTeam = false,
  why,
  chromePx = 160,
}: {
  rows: StoreRow[];
  now: Date;
  gradesState?: GradesState;
  /** The viewer is a store team: name whose audit each DM/Auditor clock is (clockName). */
  forStoreTeam?: boolean;
  /** Extra "Why it's here" column (Home's worst-10 board, owner ruling 2026-10-25). */
  why?: (row: StoreRow) => string;
  /** Height of the section around the rows (heading, help, filters, pager bar) — sizes the page. */
  chromePx?: number;
}) {
  // One district on the board (a district page, a single-district DM, a
  // grouped home section): the column only repeats it — and costs phone width.
  const showDistrict = new Set(rows.map((r) => r.store.district.trim().toLowerCase())).size > 1;
  // A page at a time, worst first as sorted (owner ruling 2026-10-29: boards keep
  // the next section's heading on screen — 7 rows at 1080p, "Show all").
  // Measured rows (UI-6 review): a board row ~92 px, a call-list row ~130 px (its "Why it's here" column
  // wraps the dates); a phone card ~220–265 px, so 2 a page there.
  // UI review 3 (E3 / D2): rows with two audit lines are taller than 92 px — the drawn rows are measured
  // and the page settles to what fits 80% of the window (heading, grade help, filters and bar = chromePx).
  const [firstGuess] = useState(() => fitRows(why ? 130 : 92, 2));
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const pageSize = useFitPageSize(bodyRef, { initial: firstGuess, chromePx, phone: 2 });
  const pager = usePager(rows, rows.map((r) => r.store.id).join(','), pageSize);
  // Phones: each row is a card (board-cards CSS) — no sideways scroll to
  // reach the grades (reflect 2026-10-25, all three personas). The ARIA
  // roles keep table meaning once CSS turns rows into grids (Safari drops it).
  return (
    <>
    <PagerTop pager={pager} />
    <div className="table-wrap board-wrap">
      <table className={why ? 'board-cards board-with-why' : 'board-cards'} role="table">
        <thead>
          <tr role="row">
            <th role="columnheader">Store</th>
            {showDistrict && <th role="columnheader" className="col-district">District</th>}
            <th role="columnheader">Timeliness</th>
            <th role="columnheader">Quality</th>
            <th role="columnheader">Audits</th>
            <th role="columnheader">Open tasks</th>
            {why && <th role="columnheader">Why it’s here</th>}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {pager.items.map((row) => {
            const { store, openTasks, overdueTasks, grade } = row;
            return (
              <tr role="row" key={store.id}>
                <td role="cell" className="card-full card-title">
                  <StoreLink store={store} />
                  {/* Its windows are longer — say so, or a long gap reads as neglect (exec round 2). */}
                  {store.longDistance && (
                    <span className="task-badge task-status-open" title={`Long-distance store: longer audit windows${ldText(row)}`}>
                      Long distance
                    </span>
                  )}
                  {/* Phones: the district rides under the store (its column is hidden). */}
                  {showDistrict && (
                    <div className="muted cell-sub phone-only">
                      <DistrictLink district={store.district} />
                    </div>
                  )}
                </td>
                {showDistrict && (
                  <td role="cell" className="col-district">
                    <DistrictLink district={store.district} />
                  </td>
                )}
                <td role="cell" data-label="Timeliness">
                  <GradeChip grade={grade} kind="timeliness" state={gradesState} row={row} />
                </td>
                <td role="cell" data-label="Quality">
                  <GradeChip grade={grade} kind="quality" state={gradesState} row={row} />
                </td>
                <td role="cell" className="card-full board-audits" data-label="Audits">
                  {/* Every started clock, worst first (DM reflect 2026-10-25); none yet → the row's "Never audited". */}
                  {row.clocks.length > 0 ? (
                    row.clocks.map((c) => (
                      <ClockLine key={`${c.template.id}:${c.party}`} clock={c} now={now} forStoreTeam={forStoreTeam} />
                    ))
                  ) : (
                    <ClockLine clock={row} now={now} forStoreTeam={forStoreTeam} />
                  )}
                </td>
                <td role="cell" className="card-full board-tasks" data-label="Open tasks">
                  {openTasks > 0 ? (
                    <Link className="entity-link" to={storePath(store.storeNumber)}>
                      {openTasks}
                      {overdueTasks > 0 && (
                        <span className="error"> ({overdueTasks} overdue)</span>
                      )}
                    </Link>
                  ) : (
                    <span className="muted">0</span>
                  )}
                </td>
                {why && (
                  <td role="cell" className="card-full board-why" data-label="Why it’s here">
                    {why(row)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <PagerBar pager={pager} noun="stores" allowAll />
    </>
  );
}
